import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import {
  selectedBranchAtom,
  userBranchesAtom,
  searchValuesAtom,
  currentDateAtom,
  reloadAtom,
  allOutcomesAtom,
} from '../../Atoms';
import {
  CALL_STANDARD_FILTERS,
  STANDARD_SORTING_OPTIONS,
} from '../../Helpers/Filters/constants';
import mapFilters from '../../Helpers/Filters/mapFilters';
import saveFiltersInLocal from '../../Helpers/Filters/saveFiltersInLocal';
import useInfiniteQuery from '../../Hooks/useInfiniteQuery';
import { download } from '../../api/utils';
import useAPI from '../../api';
import SectionPage from './SectionPage';
import ErrorDialog from '../Error';
import { formatWavePeriodValue } from '../../Utils/constants/general';
import useAbortEffect from '../../Hooks/useAbortEffect';
import formatCallStats from '../../Helpers/formatCallStats';
import formatWaveStats from '../../Helpers/formatWaveStats';
import useModuleFlag from '../../Hooks/useModuleFlag';
import useSectionLayout from '../../Hooks/useSectionLayout';
import { resolveColumns } from '../engine/columns/resolveColumns';
import {
  resolveStandardFilters,
  resolveRangeFilters,
} from '../engine/filters/resolveFilters';
import { resolveSorting } from '../engine/sorting/resolveSorting';
import { resolveSearchKeys } from '../engine/search/resolveSearch';
import LayoutEditButton from '../engine/layout/LayoutEditButton';

/** The keys the search box matches where neither the caller nor a layout says. */
const DEFAULT_SEARCH_KEYS = [
  'fullNameSearch',
  'fiscalCode',
  'mobilePhoneNumber',
];

/** The spans the list applies where neither the caller nor a layout says. */
const DEFAULT_RANGE_FILTERS = [{ keyName: 'distanceKm', defaultValue: null }];

/** What a list draws when nothing declares its columns. Held still, so its readers are too. */
const NO_COLUMNS = [];

/** Stands in for the branch until one is in scope, without being a new one each render. */
const NO_BRANCH = {};

/** The layout a page's surfaces declare for one of them, where they declare it. */
const surfaceDefault = (surfaces, surface) =>
  surfaces.find((declared) => declared.surface === surface)?.defaultLayout ??
  null;

/**
 * Route customers component wrapper with api calls:
 *
 * @param {Object} props - Object. Component's props.
 * @param {Node} props.icon - ReactElement. Section's icon for stats box.
 * @param {Boolean} props.tablet - Boolean. Media query for width.
 * @param {Array} props.userRoles - Array. Array containing logged user's roles.
 * @param {Boolean} props.heightQuery - Boolean. Media query for height.
 * @param {String} props.sectionName - String. SectionName string displayed and used for API calls.
 * @param {String} props.attributesSectionName - (Optional) String. Section whose attribute
 *   document the list should carry, when it differs from `sectionName`. Sent as the API's
 *   SectionName; the page title, stats and cache key keep using `sectionName`.
 * @param {String} props.period - (Optional) String. Represents the api call period in days.
 * @param {String} props.wavePeriod - (Optional) String. Wave period param for api calls.
 * @param {String} props.apiType - String. Represents the API call type to pass to fetch's route request.
 * @param {Boolean} props.noStats - Boolean. Section has no stats. Defaults to the
 *   HIDE_DAILY_STATS module flag when the caller does not say.
 * @param {Array} props.surfaces - (Optional) The surfaces the section configures, as
 *   `{ surface, defaultLayout }`. Each is read for the section and compiled into what the list is
 *   drawn, filtered, sorted and searched by, and the cog offers the same set. A surface the caller
 *   also passes outright - `columns`, `standardFilters`, `sortingOptions`, `searchBoxFilters` - is
 *   the caller's.
 * @param {Array} props.extraFilters - (Optional) Filters the page applies that its panel's layout
 *   does not render, in the `standardFilters` shape. Applied alongside the layout's own.
 * @param {Array} props.layoutParameters - (Optional) Runtime values the page publishes to the
 *   fields its layouts render.
 *
 * @returns {React.JSX.Element}
 */

const PageWrapper = function PageWrapper({
  statsTabs = [],
  sectionName,
  attributesSectionName = null,
  fab,
  fabVariant,
  onClickFab,
  tablet = true,
  heightQuery = true,
  period = null,
  apiType = 'SinceLastCall',
  // Undefined rather than a flag read: this is a parameter default, so it is
  // evaluated in the signature where a hook cannot be called. The flag is now the
  // fallback when a caller says nothing, resolved in the body as `hideStats`.
  noStats = undefined,
  searchBoxFilters: searchKeysProp = null,
  standardFilters: standardFiltersProp = null,
  rangeFilters: rangeFiltersProp = null,
  wavePeriod = formatWavePeriodValue(),
  sortingOptions: sortingProp = null,
  columns: columnsProp = null,
  surfaces = [],
  extraFilters = null,
  layoutParameters = [],
  headerActions = null,
  hideSearchBar = undefined,
  customFilters = null,
  sectionSubName,
  customStatusChipCallback,
  ...props
}) {
  const intl = useIntl();
  // Presentation, not capability - Module J absorbs this once configurable
  // layouts exist. An explicit prop still wins over the installation default.
  const hideDailyStats = useModuleFlag('HIDE_DAILY_STATS');
  const hideStats = noStats ?? hideDailyStats;
  const API = useAPI();
  const theme = useTheme();
  const location = useLocation()?.pathname;

  const allOutcomes = useAtomValue(allOutcomesAtom);
  const [reloadCustomers, setReload] = useAtom(reloadAtom);
  const searchValues = useAtomValue(searchValuesAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom) || NO_BRANCH;
  const userBranches = useAtomValue(userBranchesAtom);
  const { id: branchId, branchDescription: branchName } = selectedBranch;
  const currentDate = useAtomValue(currentDateAtom);
  const [stats, setStats] = useState(null);
  const [statsTabValue, setStatsTabValue] = useState(0);

  // What the page declares for each surface, and the section it is configured under. A page that
  // declares nothing reads no layout and passes what it shows in full, as it always has.
  const listDefault = surfaceDefault(surfaces, 'list');
  const filtersDefault = surfaceDefault(surfaces, 'filters');
  const sortingDefault = surfaceDefault(surfaces, 'sorting');
  const searchDefault = surfaceDefault(surfaces, 'search');
  const layoutSection = surfaces.length > 0 ? sectionName : null;

  // Read without a reload token: the editor drops what is held for the section when it saves, and
  // that is what tells every surface reading it, here and on the page a customer opens.
  const listLayout = useSectionLayout(
    listDefault ? layoutSection : null,
    'list',
    listDefault
  );

  const filtersLayout = useSectionLayout(
    filtersDefault ? layoutSection : null,
    'filters',
    filtersDefault
  );

  const sortingLayout = useSectionLayout(
    sortingDefault ? layoutSection : null,
    'sorting',
    sortingDefault
  );

  // A saved search layout with no keys hides the box, so an empty one is not a missing one.
  const searchLayout = useSectionLayout(
    searchDefault ? layoutSection : null,
    'search',
    searchDefault,
    null,
    null,
    { allowEmpty: true }
  );

  const layoutColumns = useMemo(
    () =>
      listDefault
        ? resolveColumns(listLayout, {
            context: { userBranches, selectedBranch },
          })
        : null,
    [listDefault, listLayout, userBranches, selectedBranch]
  );

  const layoutFilters = useMemo(
    () =>
      filtersDefault
        ? [...(extraFilters ?? []), ...resolveStandardFilters(filtersLayout)]
        : null,
    [filtersDefault, filtersLayout, extraFilters]
  );

  const layoutRangeFilters = useMemo(
    () => (filtersDefault ? resolveRangeFilters(filtersLayout) : null),
    [filtersDefault, filtersLayout]
  );

  const layoutSorting = useMemo(
    () => (sortingDefault ? resolveSorting(sortingLayout) : null),
    [sortingDefault, sortingLayout]
  );

  const layoutSearchKeys = useMemo(
    () => (searchDefault ? resolveSearchKeys(searchLayout) : null),
    [searchDefault, searchLayout]
  );

  // What the list is drawn and filtered by: the caller's, then the section's layout, then what a
  // customer list shows in general.
  const columns = columnsProp ?? layoutColumns ?? NO_COLUMNS;
  const standardFilters =
    standardFiltersProp ?? layoutFilters ?? CALL_STANDARD_FILTERS;
  const rangeFilters =
    rangeFiltersProp ?? layoutRangeFilters ?? DEFAULT_RANGE_FILTERS;
  const sortingOptions =
    sortingProp ?? layoutSorting ?? STANDARD_SORTING_OPTIONS;
  const searchBoxFilters =
    searchKeysProp ?? layoutSearchKeys ?? DEFAULT_SEARCH_KEYS;

  // What the list is filtered and searched by, as a value rather than as three arrays. The fetch
  // has to notice a layout that changed a key, and must not notice a caller that builds the same
  // keys inline: an array rebuilt each render would refetch on every render, and each answer
  // renders again.
  const filterKeys = JSON.stringify([
    searchBoxFilters,
    standardFilters,
    rangeFilters,
  ]);

  const downloadCustomersFile = useCallback(
    () =>
      download(
        `${sectionName}-${branchName}`,
        API.export2Excel(
          branchId,
          {
            type: apiType,
            period: period !== null ? period : currentDate.toISOString(),
            sectionName,
            wavePeriod,
          },
          {
            pageNumber: 0,
            filterExpression: mapFilters(
              searchBoxFilters,
              standardFilters,
              rangeFilters,
              searchValues
            ),
            sortExpression: searchValues?.sortValue
              ? {
                  sortValue: searchValues?.sortValue.value,
                  sort: searchValues?.sortValue.direction,
                }
              : null,
          }
        )
      ),
    [branchId, searchValues, wavePeriod, filterKeys]
  );

  const getCustomers = useCallback(
    (pageNumber) => {
      return branchId
        ? API.getCustomers(
            branchId,
            {
              type: apiType,
              period: period !== null ? period : currentDate.toISOString(),
              sectionName:
                attributesSectionName ??
                (sectionName === 'WELCOME' ? null : sectionName),
              wavePeriod: sectionName === 'SPOT' ? wavePeriod : '',
            },
            {
              pageNumber,
              filterExpression: mapFilters(
                searchBoxFilters,
                standardFilters,
                rangeFilters,
                searchValues
              ),
              sortExpression: searchValues?.sortValue
                ? {
                    sortValue: searchValues?.sortValue.value,
                    sort: searchValues?.sortValue.direction,
                  }
                : null,
              silent: pageNumber > 1,
            },
            () => {
              saveFiltersInLocal(searchValues, location);
              setReload(false);
            },
            (err) => {
              console.log(err);
              setReload(false);
            }
          )
        : Promise.resolve([]);
    },
    [
      currentDate,
      wavePeriod,
      searchValues,
      branchId,
      attributesSectionName,
      filterKeys,
    ]
  );
  const getSectionStats = (signal, tabValue) => {
    if (branchId && !hideStats && allOutcomes.length > 0) {
      setStats(null);
      const statsPeriod = statsTabs[tabValue];
      API.getStats(
        branchId,
        statsPeriod,
        sectionName === 'BIRTHDAYS' ? apiType : sectionName,
        wavePeriod,
        { silent: true, signal },
        (result) => {
          if (statsTabs[tabValue] === 'WAVE') {
            setStats(formatWaveStats(result, intl, theme));
          } else {
            setStats(formatCallStats(result, intl, allOutcomes));
          }
        },
        () => {
          setStats({});
        }
      );
    }
  };

  const getTodayCalls = useCallback(
    (pageNumber) => {
      return branchId
        ? API.todayCalls(branchId, {
            pageNumber,
            silent: true,
          })
        : Promise.resolve([]);
    },
    [branchId]
  );

  const {
    error,
    ObserverWrapper,
    removeClickedItem,
    recordCount,
    updateItem,
    reload,
  } = useInfiniteQuery(getCustomers);

  useEffect(() => {
    if (reloadCustomers) {
      reload();
    }
  }, [reloadCustomers]);

  const { ObserverWrapper: ObserverWrapperActivities } =
    useInfiniteQuery(getTodayCalls);

  useAbortEffect(
    (signal) => getSectionStats(signal, statsTabValue),
    [hideStats, wavePeriod, branchId, sectionName, statsTabValue, allOutcomes]
  );

  const handleChangeStatsTabValue = (event, newValue) => {
    setStatsTabValue(newValue);
  };

  return (
    <>
      {error && <ErrorDialog error={error} />}
      <SectionPage
        customStatusChipCallback={customStatusChipCallback}
        columns={columns}
        stats={stats}
        tabs={statsTabs}
        tabValue={statsTabValue}
        onChangeTabValue={handleChangeStatsTabValue}
        tablet={tablet}
        hideStats={hideStats}
        updateItem={updateItem}
        sectionName={sectionName}
        heightQuery={heightQuery}
        filters={[...rangeFilters, ...standardFilters]}
        sortingOptions={sortingOptions}
        ExportToExcel={downloadCustomersFile}
        ListObserverWrapper={ObserverWrapper}
        mainBoxWidth={hideStats ? '100%' : '70%'}
        StatsObserverWrapper={ObserverWrapperActivities}
        removeFromList={(id) => removeClickedItem(id, recordCount - 1)}
        wavePeriod={wavePeriod}
        sectionSubName={sectionSubName}
        fab={fab}
        fabVariant={fabVariant}
        onClickFab={onClickFab}
        // The layouts the page reads are the ones its cog configures, so a page that reads none
        // shows no cog and a caller resolving its own supplies its own.
        headerActions={
          headerActions ??
          (surfaces.length > 0 ? (
            <LayoutEditButton
              sectionName={sectionName}
              surfaces={surfaces}
              parameters={layoutParameters}
            />
          ) : null)
        }
        hideSearchBar={hideSearchBar ?? searchBoxFilters.length === 0}
        // The panel shows the layout the list is filtered by, so removing a filter from the one
        // removes it from the other.
        filtersLayout={filtersDefault ? filtersLayout : null}
        customFilters={
          customFilters && filtersDefault
            ? React.cloneElement(customFilters, { layout: filtersLayout })
            : customFilters
        }
        {...props}
      />
    </>
  );
};

export default PageWrapper;
